'use client';

import { createClient } from '@/lib/supabase/client';
import { sendTransactionalEmail } from '@/lib/services/emailService';

export interface WatchlistEntry {
  id: string;
  user_id: string;
  project_id: string;
  created_at: string;
}

export interface WatchlistProject {
  id: string;
  user_id: string;
  project_id: string;
  created_at: string;
  projects: {
    id: string;
    project_name: string;
    developer_name: string;
    location: string;
    micro_market: string;
    price_per_sqft: number;
    inventory_sold_pct: number;
    project_status: string;
    image_url: string;
    slug: string;
  };
}

const supabase = createClient();

export const watchlistService = {
  async getWatchlist(userId: string): Promise<WatchlistProject[]> {
    const { data, error } = await supabase
      .from('project_watchlist')
      .select(`
        id,
        user_id,
        project_id,
        created_at,
        projects (
          id,
          project_name,
          developer_name,
          location,
          micro_market,
          price_per_sqft,
          inventory_sold_pct,
          project_status,
          image_url,
          slug
        )
      `)
      .eq('user_id', userId)
      .order('created_at', { ascending: false });

    if (error) {
      console.error('[watchlistService.getWatchlist]', error.message);
      return [];
    }
    return (data as WatchlistProject[]) || [];
  },

  async isWatched(userId: string, projectId: string): Promise<boolean> {
    const { data, error } = await supabase
      .from('project_watchlist')
      .select('id')
      .eq('user_id', userId)
      .eq('project_id', projectId)
      .maybeSingle();

    if (error) return false;
    return !!data;
  },

  async addToWatchlist(userId: string, projectId: string): Promise<boolean> {
    const { error } = await supabase
      .from('project_watchlist')
      .insert({ user_id: userId, project_id: projectId });

    if (error) {
      console.error('[watchlistService.addToWatchlist]', error.message);
      return false;
    }
    return true;
  },

  async removeFromWatchlist(userId: string, projectId: string): Promise<boolean> {
    const { error } = await supabase
      .from('project_watchlist')
      .delete()
      .eq('user_id', userId)
      .eq('project_id', projectId);

    if (error) {
      console.error('[watchlistService.removeFromWatchlist]', error.message);
      return false;
    }
    return true;
  },

  async getWatchlistProjectIds(userId: string): Promise<string[]> {
    const { data, error } = await supabase
      .from('project_watchlist')
      .select('project_id')
      .eq('user_id', userId);

    if (error) return [];
    return (data || []).map((row: { project_id: string }) => row.project_id);
  },
};

export async function sendWatchlistAlertEmail(params: {
  to: string;
  fullName: string;
  projectName: string;
  developerName: string;
  changeType: 'price' | 'inventory';
  oldValue: string;
  newValue: string;
  projectSlug: string;
}): Promise<void> {
  await sendTransactionalEmail({
    type: 'watchlist_alert',
    to: params.to,
    params: {
      fullName: params.fullName,
      projectName: params.projectName,
      developerName: params.developerName,
      changeType: params.changeType,
      oldValue: params.oldValue,
      newValue: params.newValue,
      projectSlug: params.projectSlug,
    },
  });
}
